import React, { useEffect } from "react";

/**
 * ReleaseNotesModal — the dismissible "What's New" overlay for v2.
 *
 * Presentational only: open-state is owned by useReleaseNotes (see
 * ReleaseNotesContext). A product-led walkthrough — hero + feature highlights,
 * each with an optional status badge and a "try this" nudge.
 *
 * Props:
 *   open         — whether the modal is visible
 *   onClose      — called on Got-it / [×] / Escape / backdrop click
 *   notes        — { title, subtitle, highlights: [{icon,title,body,tryIt?,badge?}] }
 *   onStartTour? — optional; when supplied, a secondary "Take a tour" button is
 *                  rendered (Phase 2 coachmark tour). Omitted → no dead button.
 */
const ReleaseNotesModal = ({ open, onClose, notes, onStartTour = null }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !notes) return null;

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  return (
    <div
      className="release-modal__backdrop"
      data-testid="v2-release-modal-backdrop"
      onClick={onBackdrop}
    >
      <div
        className="release-modal"
        data-testid="v2-release-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="release-modal-title"
      >
        <button
          type="button"
          className="release-modal__close"
          data-testid="v2-release-modal-close"
          aria-label="Dismiss what's new"
          onClick={() => onClose?.()}
        >
          ✕
        </button>

        <div className="release-modal__header">
          <div className="release-modal__spark" aria-hidden="true">✦</div>
          <h2 className="release-modal__title" id="release-modal-title">{notes.title}</h2>
          {notes.subtitle && <p className="release-modal__subtitle">{notes.subtitle}</p>}
        </div>

        <div className="release-modal__body">
          {(notes.highlights || []).map((h, i) => (
            <div className="release-highlight" data-testid="v2-release-highlight" key={h.title || i}>
              <div className="release-highlight__icon" aria-hidden="true">{h.icon}</div>
              <div className="release-highlight__main">
                <div className="release-highlight__head">
                  <span className="release-highlight__title">{h.title}</span>
                  {h.badge && <span className="release-badge">{h.badge}</span>}
                </div>
                <div className="release-highlight__body">{h.body}</div>
                {h.tryIt && (
                  <div className="release-highlight__try">
                    <span className="release-highlight__try-label">Try it</span>
                    {h.tryIt}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="release-modal__footer">
          {onStartTour && (
            <button
              type="button"
              className="release-modal__btn release-modal__btn--ghost"
              data-testid="v2-release-modal-tour"
              onClick={() => onStartTour()}
            >
              Take a tour
            </button>
          )}
          <button
            type="button"
            className="release-modal__btn release-modal__btn--primary"
            data-testid="v2-release-modal-gotit"
            onClick={() => onClose?.()}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReleaseNotesModal;
